import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "@client-app/i18n";
import { authService } from "@common/api/authService";
import {
  getCurrentProductCode,
  PRODUCT_CONTEXT_HEADER,
} from "@common/api/productContext";
import { useAuth } from "@/contexts/AuthContext";
import { readShopifyInstallContext } from "@/flows/shopifyInstall";
import {
  assertInstallWorkspace,
  buildOnboardingInstallResumeRoute,
  isInlineShopifyReturn,
  clearOnboardingInstall,
  hasShopifyCallbackError,
  isFullPageShopifyReturn,
  markOnboardingInstallVerified,
  matchesOnboardingReturn,
  readOnboardingInstall,
  runOnboardingInstall,
  verifyOnboardingInstallation,
} from "@/flows/shopifyOnboardingInstall";

/** Owns the scoped same-tab return for onboarding and Admin connection entries. */
export default function OnboardingShopifyInstall({
  workspaceId,
  scene = "onboarding",
  onProgress,
  onConnected,
  onRestart,
  renderStatus = (status) => status,
}: {
  scene?: "onboarding" | "admin";
  workspaceId?: string;
  onProgress?: (state: "checking" | "error" | "idle") => void;
  onConnected?: () => Promise<unknown>;
  onRestart?: () => void;
  renderStatus?: (status: React.ReactNode) => React.ReactNode;
} = {}) {
  const { user, isLoadingProfile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const accountId = user?.id || "";
  const productCode = getCurrentProductCode();
  const hasReturn = isInlineShopifyReturn(location.search);
  const [state, setState] = useState<"checking" | "error" | "idle">(
    hasReturn ? "checking" : "idle",
  );
  const callbacks = useRef({ onProgress, onConnected, onRestart });
  callbacks.current = { onProgress, onConnected, onRestart };
  useEffect(() => {
    callbacks.current.onProgress?.(state);
  }, [state]);
  const [retry, setRetry] = useState(0);
  const restartRequired = useRef(false);
  const latest = useRef({
    accountId,
    productCode,
    workspaceId,
    search: location.search,
  });
  latest.current = {
    accountId,
    productCode,
    workspaceId,
    search: location.search,
  };
  useEffect(() => {
    if (!hasReturn) {
      setState("idle");
      return;
    }
    if (
      !accountId ||
      isLoadingProfile ||
      (workspaceId !== undefined && !workspaceId)
    )
      return;
    const controller = new AbortController();
    const scope = {
      accountId,
      productCode,
      workspaceId,
      search: location.search,
    };
    const isCurrent = () =>
      !controller.signal.aborted &&
      JSON.stringify(latest.current) === JSON.stringify(scope);
    const config = {
      signal: controller.signal,
      headers: { [PRODUCT_CONTEXT_HEADER]: productCode },
    };
    restartRequired.current = false;
    setState("checking");
    // Defer kickoff one task so StrictMode setup/cleanup does not duplicate POSTs.
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const storage = window.sessionStorage;
          const intent = readOnboardingInstall(storage, scope);
          if (
            !intent ||
            intent.scene !== scene ||
            (workspaceId && intent.workspaceId !== workspaceId) ||
            !matchesOnboardingReturn(intent, scope.search)
          ) {
            restartRequired.current = true;
            throw new Error("Invalid installation return");
          }
          const requestConfig = {
            ...config,
            headers: {
              ...config.headers,
              "X-Workspace-Id": intent.workspaceId,
            },
          };
          const complete = async (correlated: typeof intent) => {
            const route = await verifyOnboardingInstallation(
              correlated,
              () => authService.getWorkspaceIntegrations(requestConfig),
              controller.signal,
            );
            if (!isCurrent()) return;
            await callbacks.current.onConnected?.();
            if (!isCurrent()) return;
            if (correlated.scene === "admin") clearOnboardingInstall(storage);
            else markOnboardingInstallVerified(storage, correlated);
            setState("idle");
            navigate(route, { replace: true });
          };
          if (isFullPageShopifyReturn(scope.search)) {
            if (hasShopifyCallbackError(scope.search)) {
              restartRequired.current = true;
              throw new Error("Shopify authorization failed");
            }
            const workspace = await authService.getShopifyInstallWorkspace(
              intent.workspaceId,
              productCode,
              controller.signal,
            );
            if (!isCurrent()) return;
            assertInstallWorkspace(workspace, intent.workspaceId);
            await complete(intent);
          } else {
            const result = await runOnboardingInstall({
              storage,
              intent,
              ticket: readShopifyInstallContext(scope.search),
              origin: window.location.origin,
              attemptId: window.crypto.randomUUID(),
              signal: controller.signal,
              workspace: () =>
                authService.getShopifyInstallWorkspace(
                  intent.workspaceId,
                  productCode,
                  controller.signal,
                ),
              resolve: (ticket) =>
                authService.resolveShopifyInstallContext(ticket, requestConfig),
              connect: (request) =>
                authService.connectShopify({
                  ...request,
                  axiosRequestConfig: requestConfig,
                }),
              redirect: (url) => {
                if (isCurrent()) window.location.assign(url);
              },
            });
            if (result.alreadyConnected && isCurrent()) {
              const correlated = readOnboardingInstall(storage, scope);
              if (!correlated) throw new Error("Installation return expired");
              await complete(correlated);
            }
          }
        } catch {
          if (isCurrent()) setState("error");
        }
      })();
    }, 0);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [
    hasReturn,
    scene,
    workspaceId,
    accountId,
    productCode,
    location.search,
    isLoadingProfile,
    retry,
    navigate,
  ]);
  const cancel = () => {
    const intent = readOnboardingInstall(window.sessionStorage, {
      accountId,
      productCode,
    });
    clearOnboardingInstall(window.sessionStorage);
    navigate(
      intent
        ? buildOnboardingInstallResumeRoute(intent)
        : scene === "admin"
          ? "/integrations"
          : "/onboarding",
      { replace: true },
    );
  };
  if (state === "idle") return null;
  return renderStatus(
    <div className="px-5 pb-4 text-sm text-[#666F8D]">
      <p role={state === "error" ? "alert" : "status"} aria-live="polite">
        {t(
          state === "error"
            ? "website.shopifyInstall.retry"
            : "website.shopifyInstall.checking",
        )}
      </p>
      {state === "error" && (
        <div className="flex gap-4 mt-6">
          <button
            type="button"
            onClick={() => {
              if (
                restartRequired.current ||
                !readOnboardingInstall(window.sessionStorage, {
                  accountId,
                  productCode,
                })
              ) {
                cancel();
                callbacks.current.onRestart?.();
              } else setRetry((value) => value + 1);
            }}
            className="rounded-lg bg-[#1A162F] px-4 py-2 text-white"
          >
            {t("website.shopifyInstall.retryButton")}
          </button>
          <button type="button" onClick={cancel}>
            {t("common.cancel")}
          </button>
        </div>
      )}
    </div>
  );
}
